/**
 * Personal-accounts rollout flag — shared query seam + synchronous snapshot.
 *
 * The self-serve personal-account experience is gated by a single GLOBAL
 * feature flag (`personal_accounts`). The organization-setup wizard needs to
 * know its value on the very first paint so it can either show the
 * work-vs-personal fork (flag ON) or skip straight to naming a work org
 * (flag OFF) — with no skeleton flash.
 *
 * Two mechanisms make that instant, mirroring the personal-theme.ts pattern:
 *
 *  1. Boot prefetch (prefetchPersonalAccountsFlag): fired once the user is
 *     authenticated (App Root), so by the time they reach the setup page the
 *     value is already warm in the React Query cache. The flag flips rarely,
 *     so the query uses a generous stale time.
 *
 *  2. localStorage snapshot: the last resolved value is cached synchronously
 *     (readable before React renders). The setup page seeds react-query with
 *     it as `placeholderData` — instant correct paint on a cold page — while
 *     still fetching to correct a stale snapshot. The snapshot is (re)written
 *     wherever the query resolves (fetchPersonalAccountsFlag), so the boot
 *     prefetch and the page's own query both keep it fresh; a flag flipped off
 *     later is corrected on the next fetch, which repaints the snapshot for the
 *     following boot.
 *
 * Safe defaults: an unknown value (no snapshot, no cache — first-ever visit)
 * reads as `undefined`, which the setup page treats as work-only-while-loading
 * (personal card hidden). Server enforcement is unchanged and independent —
 * create() still 403s a PERSONAL org when the flag is off regardless of this
 * client snapshot.
 */

import type { QueryClient } from '@tanstack/react-query'

import { featureFlagsApi } from '@/lib/api/feature-flags.api'

export const PERSONAL_ACCOUNTS_FLAG_KEY = 'personal_accounts'

/** Shared query key — identical for the boot prefetch and the setup page. */
export const PERSONAL_ACCOUNTS_FLAG_QUERY_KEY = [
  'feature-flags',
  'global',
  PERSONAL_ACCOUNTS_FLAG_KEY,
] as const

/** The flag flips rarely; keep the cached value fresh for a long window. */
export const PERSONAL_ACCOUNTS_FLAG_STALE_TIME = 30 * 60 * 1000

const SNAPSHOT_KEY = 'scrambl-personal-accounts-enabled'

/**
 * Synchronous last-known value from localStorage. Returns `undefined` when no
 * snapshot has ever been written (first-ever visit) or when storage is
 * unavailable — callers treat `undefined` as "unknown → work-only while
 * loading".
 */
export function readPersonalAccountsSnapshot(): boolean | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = window.localStorage.getItem(SNAPSHOT_KEY)
    if (raw === 'true') return true
    if (raw === 'false') return false
    return undefined
  } catch {
    return undefined
  }
}

function writePersonalAccountsSnapshot(isEnabled: boolean): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SNAPSHOT_KEY, isEnabled ? 'true' : 'false')
  } catch {
    // Storage unavailable (private mode / quota) — the query cache still holds
    // the value for this session; only the cross-boot snapshot is lost.
  }
}

/**
 * The single queryFn used by both the boot prefetch and the setup page. It
 * fetches the global flag and writes the resolved value to the localStorage
 * snapshot so the next cold boot paints correctly.
 */
export async function fetchPersonalAccountsFlag(): Promise<{
  isEnabled: boolean
}> {
  const result = await featureFlagsApi.checkGlobal(PERSONAL_ACCOUNTS_FLAG_KEY)
  writePersonalAccountsSnapshot(result.isEnabled)
  return result
}

/**
 * Warm the flag into the shared React Query cache at app boot (once the user is
 * authenticated). Safe to call repeatedly — react-query dedupes and respects
 * the stale time. Failures are swallowed: the setup page falls back to the
 * snapshot / safe default.
 */
export async function prefetchPersonalAccountsFlag(
  queryClient: QueryClient,
): Promise<void> {
  try {
    await queryClient.prefetchQuery({
      queryKey: PERSONAL_ACCOUNTS_FLAG_QUERY_KEY,
      queryFn: fetchPersonalAccountsFlag,
      staleTime: PERSONAL_ACCOUNTS_FLAG_STALE_TIME,
    })
  } catch {
    // Non-fatal — onboarding still resolves the flag on demand.
  }
}
